/* ========================================================================
   カート checkout 共通ユーティリティ
   cart-notification / cart drawer / cart page で重複していた
   checkout 直前の準備処理を集約する。
   ======================================================================== */
window.AuremeCheckoutUtils = (() => {
  const CHECKOUT_CONTEXT_KEY = 'auremeCartCheckoutContext';
  const CHECKOUT_READY_FLAG = 'auremeCheckoutContextReady';
  /* 購入可能数は 99 点まで。100 は「達したら超過」と判定する閾値として使う */
  const CART_ITEM_LIMIT = 100;
  const CART_ITEM_LIMIT_ERROR_MESSAGE =
    '※一度の注文における購入可能数は99点までです。数量の再計算、または対象商品をカートから削除してください。';
  const POINT_AMOUNT_CART_ATTRIBUTE = '利用ポイント';
  const GIFT_CARD_CART_ATTRIBUTE = 'ギフトカードコード';
  const PUBLIC_SALE_STATUS_PROPERTY = '販売ステータス';
  const PUBLIC_PREORDER_DELIVERY_DATE_PROPERTY = '予約品お届け予定日';
  const PREORDER_STATUS_VALUE = '予約商品';
  const NORMAL_STATUS_VALUE = '通常商品';
  const CHECKOUT_LINE_ITEM_PROPERTY_UPDATE_ERROR_MESSAGE =
    'checkout用の商品情報補完に失敗しました。';
  const SYNCED_CART_UPDATE_CONTROL_SELECTOR = '[data-aureme-synced-cart-update]';
  const CHECKOUT_PREPARATION_ERROR_MESSAGE =
    'カート情報の更新に失敗しました。\nページを再読み込みしてお試しください。';

  function parseFooterStateElement(footerStateElement) {
    if (!footerStateElement) return null;

    return {
      deliveryOptionsEnabled: footerStateElement.dataset.deliveryOptionsEnabled === 'true',
      storedDeliveryDate: footerStateElement.dataset.storedDeliveryDate || '',
      storedDeliveryTime: footerStateElement.dataset.storedDeliveryTime || '',
    };
  }

  function getDocumentFooterState() {
    return parseFooterStateElement(document.querySelector('[data-cart-footer-state]'));
  }

  async function fetchFooterState() {
    const footerState = getDocumentFooterState();
    if (footerState) return footerState;

    const response = await fetch(`${routes.cart_url}?section_id=main-cart-footer`, {
      headers: {
        Accept: 'text/html',
      },
    });
    if (!response.ok) {
      throw new Error(`main-cart-footer request failed: ${response.status}`);
    }

    const sectionHtml = await response.text();
    const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
    return parseFooterStateElement(parsedHtml.querySelector('[data-cart-footer-state]'));
  }

  async function fetchCartState() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`cart.js request failed: ${response.status}`);
    }

    return response.json();
  }

  async function prepareDeliveryAttributesForCheckout({
    cartState,
    updateAttributes,
    isSyncing,
    setSyncing,
  }) {
    if (
      !cartState ||
      typeof updateAttributes !== 'function' ||
      typeof setSyncing !== 'function' ||
      isSyncing
    ) {
      return cartState;
    }

    const storedDeliveryDate = cartState.attributes?.配送指定日 || '';
    const storedDeliveryTime = cartState.attributes?.配送時間指定 || '';

    if (storedDeliveryDate === '' && storedDeliveryTime === '') return cartState;

    /* 現在描画中の footer state を優先し、存在しない場合のみ section を再取得する */
    const footerState = await fetchFooterState();
    if (!footerState || footerState.deliveryOptionsEnabled) return cartState;

    setSyncing(true);

    try {
      await updateAttributes({
        ...(cartState.attributes || {}),
        配送指定日: '',
        配送時間指定: '',
      });
    } finally {
      setSyncing(false);
    }

    return cartState;
  }

  function getAuremeCustomerIdString() {
    if (typeof AUREME_CUSTOMER_ID === 'undefined' || AUREME_CUSTOMER_ID === null) return '';
    return String(AUREME_CUSTOMER_ID).trim();
  }

  function normalizePointAttributeInt(value) {
    return Math.max(0, parseInt(value, 10) || 0);
  }

  /** sanyo_app_use_point_* は ja-JP 区切りで保存（aureme-cart-enhancements と同一） */
  function readSanyoUsePointFromLocalStorage(customerId) {
    if (!customerId) return 0;
    const raw = (localStorage.getItem(`sanyo_app_use_point_${customerId}`) || '').trim();
    return normalizePointAttributeInt(parseInt(raw.replace(/,/g, ''), 10));
  }

  function normalizeCartAttrString(value) {
    if (value == null || value === false) return '';
    return String(value).trim();
  }

  /*
   * Shopify では `_` 始まりの property が非表示扱いのため、旧実装の hidden property
   * から引き継いだ予定日の先頭 `_` を取り除き、公開表記として成立させる。
   */
  function normalizePreorderDeliveryDate(value) {
    const normalizedValue = normalizeCartAttrString(value);
    if (!normalizedValue) return '';

    const parts = normalizedValue.split('_');
    while (parts[0] === '') {
      parts.shift();
    }

    return parts.join('_');
  }

  function getLineItemProperties(item) {
    if (
      !item?.properties ||
      typeof item.properties !== 'object' ||
      Array.isArray(item.properties)
    ) {
      return {};
    }

    return item.properties;
  }

  /*
   * checkout の注文サマリーへ出す公開 property だけを正規化する。
   * 予約以外の商品や空の予定日は削除し、旧 `_` property を公開値として復活させない。
   */
  function getCheckoutLineItemPropertiesUpdate(item) {
    const properties = getLineItemProperties(item);
    const nextProperties = { ...properties };

    const publicSaleStatus = normalizeCartAttrString(properties[PUBLIC_SALE_STATUS_PROPERTY]);
    const publicDeliveryDate = normalizeCartAttrString(
      properties[PUBLIC_PREORDER_DELIVERY_DATE_PROPERTY],
    );
    let nextPublicSaleStatus = '';
    let nextPublicDeliveryDate = '';

    if (publicSaleStatus === PREORDER_STATUS_VALUE) {
      nextPublicSaleStatus = PREORDER_STATUS_VALUE;
      nextPublicDeliveryDate = normalizePreorderDeliveryDate(publicDeliveryDate);
    } else if (publicSaleStatus === NORMAL_STATUS_VALUE) {
      nextPublicSaleStatus = NORMAL_STATUS_VALUE;
    }

    if (nextPublicSaleStatus) {
      nextProperties[PUBLIC_SALE_STATUS_PROPERTY] = nextPublicSaleStatus;
    } else {
      delete nextProperties[PUBLIC_SALE_STATUS_PROPERTY];
    }
    if (nextPublicDeliveryDate) {
      nextProperties[PUBLIC_PREORDER_DELIVERY_DATE_PROPERTY] = nextPublicDeliveryDate;
    } else {
      delete nextProperties[PUBLIC_PREORDER_DELIVERY_DATE_PROPERTY];
    }

    return {
      properties: nextProperties,
      shouldUpdate:
        publicSaleStatus !== nextPublicSaleStatus || publicDeliveryDate !== nextPublicDeliveryDate,
    };
  }

  function findNextCheckoutLineItemPropertiesUpdate(cartState) {
    if (!Array.isArray(cartState?.items)) return null;

    for (const [index, item] of cartState.items.entries()) {
      const update = getCheckoutLineItemPropertiesUpdate(item);
      if (update.shouldUpdate) {
        return { index, item, update };
      }
    }

    return null;
  }

  async function ensureCheckoutLineItemPropertiesForCheckout(cartState) {
    if (!Array.isArray(cartState?.items) || cartState.items.length === 0) return cartState;
    if (
      typeof routes === 'undefined' ||
      !routes.cart_change_url ||
      typeof fetchConfig !== 'function'
    ) {
      console.error(CHECKOUT_LINE_ITEM_PROPERTY_UPDATE_ERROR_MESSAGE);
      return cartState;
    }

    let latestCartState = cartState;
    // 1行ずつ更新し都度 cart state を取り直すため、更新後の行結合を見込んだ上限で無限ループを防ぐ
    const maxUpdateCount = Math.max(cartState.items.length * 2, 10);

    for (let updateCount = 0; updateCount < maxUpdateCount; updateCount += 1) {
      const nextUpdate = findNextCheckoutLineItemPropertiesUpdate(latestCartState);
      if (!nextUpdate) return latestCartState;

      const payload = {
        properties: nextUpdate.update.properties,
        quantity: normalizePointAttributeInt(nextUpdate.item.quantity),
      };
      const itemKey = normalizeCartAttrString(nextUpdate.item.key);
      if (itemKey) {
        payload.id = itemKey;
      } else {
        payload.line = nextUpdate.index + 1;
      }

      /*
       * checkout の注文サマリーに表示する公開 property だけを補完し、
       * 行の merge に備えて更新後の cart state をもとに次の対象行を探す。
       */
      try {
        const response = await fetch(`${routes.cart_change_url}.js`, {
          ...fetchConfig(),
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          console.error(CHECKOUT_LINE_ITEM_PROPERTY_UPDATE_ERROR_MESSAGE);
          return latestCartState;
        }

        latestCartState = await response.json();
      } catch (error) {
        console.error(CHECKOUT_LINE_ITEM_PROPERTY_UPDATE_ERROR_MESSAGE, error);
        return latestCartState;
      }
    }

    console.error(CHECKOUT_LINE_ITEM_PROPERTY_UPDATE_ERROR_MESSAGE);
    return latestCartState;
  }

  function buildCartAttributesClearingPointAmount(attributes) {
    return {
      ...(attributes || {}),
      [POINT_AMOUNT_CART_ATTRIBUTE]: '',
    };
  }

  /* ポイント利用で発行したギフトカードは discount application の title にコードがそのまま入る */
  function isPointDiscountApplication(application, giftCardCode) {
    const gift = normalizeCartAttrString(giftCardCode).toLowerCase();
    if (!gift) return false;

    const title = normalizeCartAttrString(application?.title).toLowerCase();
    return title !== '' && title === gift;
  }

  function getCentsAsYen(value) {
    if (value === undefined || value === null || value === '') return 0;
    const cents = Number(value);
    if (!Number.isFinite(cents)) return 0;
    return normalizePointAttributeInt(Math.round(cents / 100));
  }

  function getPointDiscountAmountYen(cart, giftCardCode) {
    let allocationAmount = 0;
    if (Array.isArray(cart?.items)) {
      // 二重計上を避けるため、商品ごとの discount 配賦を先に集計する。
      for (const item of cart.items) {
        if (!Array.isArray(item?.discount_allocations)) continue;
        for (const allocation of item.discount_allocations) {
          if (isPointDiscountApplication(allocation?.discount_application, giftCardCode)) {
            allocationAmount += getCentsAsYen(allocation?.amount);
          }
        }
      }
    }
    if (allocationAmount > 0) return allocationAmount;

    if (!Array.isArray(cart?.cart_level_discount_applications)) return 0;

    return cart.cart_level_discount_applications.reduce((sum, application) => {
      if (!isPointDiscountApplication(application, giftCardCode)) return sum;
      return sum + getCentsAsYen(application?.total_allocated_amount);
    }, 0);
  }

  function hasPointApiConfig() {
    return (
      typeof SANYO_APP_PROXY_PATH !== 'undefined' &&
      Boolean(SANYO_APP_PROXY_PATH) &&
      typeof AUREME_STORE_DOMAIN !== 'undefined' &&
      Boolean(AUREME_STORE_DOMAIN)
    );
  }

  function pointErrorFlagIsSet(errorFlg) {
    if (errorFlg === undefined || errorFlg === null) return false;
    return Number(errorFlg) !== 0;
  }

  async function assertPointGiftCardCanBeUsedForCheckout({
    cart,
    customerId,
    giftCardCode,
    usePoint,
  } = {}) {
    const cartToken = normalizeCartAttrString(cart?.token);
    const storedCartToken = customerId
      ? normalizeCartAttrString(localStorage.getItem(`sanyo_app_cart_token_${customerId}`) || '')
      : '';

    /*
     * cart attribute は checkout へ渡るため、ブラウザ側で改変可能な DOM/LS 値だけではなく
     * App Proxy 側の利用可否確認と、ポイント利用時に保存した cart token の一致を必須にする。
     */
    if (
      !customerId ||
      !giftCardCode ||
      !cartToken ||
      !storedCartToken ||
      cartToken !== storedCartToken
    ) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }
    if (!hasPointApiConfig()) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }

    const response = await fetch(
      `https://${AUREME_STORE_DOMAIN}${SANYO_APP_PROXY_PATH}/point/getlimitinfo`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          giftCardCode,
          cartToken,
          customerId,
          usePoint: String(usePoint),
        }),
      },
    );
    if (!response.ok) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }

    const data = await response.json();
    if (pointErrorFlagIsSet(data.error_flg) || !data.enable_flg) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }
  }

  /**
   * 会員ポイント利用で発行したギフトカードをチェックアウトで効かせるため、
   * sanyo/crest と同様にギフトカードコードだけを Shopify カートへ書き込む。
   * カートページの form 外（ドロワー・カート通知）から購入する場合でも属性が欠けないようにする。
   */
  async function ensurePointCartAttributesForCheckout() {
    const customerId = getAuremeCustomerIdString();

    let gift = customerId
      ? normalizeCartAttrString(document.querySelector('[data-cart-points-giftcard]')?.value || '')
      : '';
    if (!gift && customerId) {
      gift = normalizeCartAttrString(
        localStorage.getItem(`sanyo_app_gift_card_code_${customerId}`) || '',
      );
    }

    let applied = customerId
      ? normalizePointAttributeInt(
          parseInt(document.querySelector('[data-cart-points-hidden]')?.value || '0', 10),
        )
      : 0;
    if (applied <= 0 && customerId) {
      applied = readSanyoUsePointFromLocalStorage(customerId);
    }

    if (typeof routes === 'undefined' || !routes.cart_url || !routes.cart_update_url) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }
    if (typeof fetchConfig !== 'function') {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }

    const cart = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    }).then((response) => {
      if (!response.ok) throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
      return response.json();
    });

    const prev =
      cart.attributes && typeof cart.attributes === 'object' && !Array.isArray(cart.attributes)
        ? cart.attributes
        : {};

    const prevGift = normalizeCartAttrString(prev[GIFT_CARD_CART_ATTRIBUTE]);
    const hasPointAmountAttribute = Object.hasOwn(prev, POINT_AMOUNT_CART_ATTRIBUTE);
    const giftForCheckout = gift || prevGift;
    let appliedForCheckout = applied;
    if (appliedForCheckout <= 0 && giftForCheckout) {
      appliedForCheckout = getPointDiscountAmountYen(cart, giftForCheckout);
    }
    const shouldSyncGift = giftForCheckout !== '' && appliedForCheckout > 0;
    const shouldClearGift = prevGift !== '' && !shouldSyncGift;

    if (!shouldSyncGift && !hasPointAmountAttribute && !shouldClearGift) {
      return;
    }

    if (shouldSyncGift) {
      await assertPointGiftCardCanBeUsedForCheckout({
        cart,
        customerId,
        giftCardCode: giftForCheckout,
        usePoint: appliedForCheckout,
      });
    }

    const attributes = buildCartAttributesClearingPointAmount(prev);
    if (shouldSyncGift) {
      attributes[GIFT_CARD_CART_ATTRIBUTE] = giftForCheckout;
    } else if (shouldClearGift) {
      attributes[GIFT_CARD_CART_ATTRIBUTE] = '';
    }

    if (shouldSyncGift && prevGift === giftForCheckout && !hasPointAmountAttribute) return;

    const response = await fetch(`${routes.cart_update_url}.js`, {
      ...fetchConfig(),
      body: JSON.stringify({ attributes }),
    });
    if (!response.ok) {
      throw new Error(CHECKOUT_PREPARATION_ERROR_MESSAGE);
    }
  }

  function getCheckoutPreparationErrorMessage() {
    return CHECKOUT_PREPARATION_ERROR_MESSAGE;
  }

  function getCartFormItemCount(form, cartState) {
    const updateControls = getCartUpdateControls(form);

    if (updateControls.length === 0) return null;

    const formItemCount = updateControls.reduce(
      (sum, control) => sum + normalizePointAttributeInt(control.value),
      0,
    );

    /*
     * form 側に描画されていない行がある場合は cart.js の item_count を下限にし、
     * checkout 直前の数量上限判定で未描画行を落とさない。
     */
    if (Array.isArray(cartState?.items) && updateControls.length < cartState.items.length) {
      return Math.max(normalizePointAttributeInt(cartState?.item_count), formItemCount);
    }

    return formItemCount;
  }

  function getCartUpdateControls(form) {
    const connectedForm = resolveConnectedElement(form, HTMLFormElement);
    if (!connectedForm) return [];

    return [
      ...connectedForm.querySelectorAll('input[name^="updates["], select[name^="updates["]'),
    ].filter(
      (control) =>
        /^updates\[[^\]]*\]$/.test(control.name) &&
        !control.matches(SYNCED_CART_UPDATE_CONTROL_SELECTOR),
    );
  }

  function syncCartFormUpdateControlsWithCartState(form, cartState) {
    const connectedForm = resolveConnectedElement(form, HTMLFormElement);
    if (!connectedForm) return;

    for (const control of connectedForm.querySelectorAll(SYNCED_CART_UPDATE_CONTROL_SELECTOR)) {
      control.remove();
    }

    const cartItems = cartState?.items;
    if (!Array.isArray(cartItems)) return;

    const updateControls = getCartUpdateControls(connectedForm);
    if (updateControls.length === 0 || updateControls.length === cartItems.length) return;

    /*
     * property 補完で line item が merge された場合、
     * 古い updates[] が checkout 直前に数量を戻さないよう最新行で作り直す。
     */
    for (const control of updateControls) {
      control.disabled = true;
    }

    for (const item of cartItems) {
      const updateControl = document.createElement('input');
      updateControl.type = 'hidden';
      updateControl.name = 'updates[]';
      updateControl.value = String(normalizePointAttributeInt(item.quantity));
      updateControl.dataset.auremeSyncedCartUpdate = 'true';
      connectedForm.appendChild(updateControl);
    }
  }

  function resolveCheckoutItemCount({ cartState, form } = {}) {
    const cartItemCount = normalizePointAttributeInt(cartState?.item_count);
    const formItemCount = getCartFormItemCount(form, cartState);

    if (formItemCount === null) return cartItemCount;

    return formItemCount;
  }

  async function validateCartItemLimitForCheckout(options = {}) {
    const { onLimitExceeded, form } =
      typeof options === 'function' ? { onLimitExceeded: options } : options || {};
    const cartState = await fetchCartState();
    const itemCount = resolveCheckoutItemCount({ cartState, form });

    if (itemCount < CART_ITEM_LIMIT) return cartState;

    if (typeof onLimitExceeded === 'function') {
      onLimitExceeded(cartState);
    }

    throw new Error(CART_ITEM_LIMIT_ERROR_MESSAGE);
  }

  async function prepareAndResumeCheckoutSubmit({
    form,
    prepareDeliveryAttributes,
    onCartItemLimitExceeded,
    recordContext,
    resumeSubmit,
  } = {}) {
    try {
      if (
        typeof prepareDeliveryAttributes !== 'function' ||
        typeof recordContext !== 'function' ||
        typeof resumeSubmit !== 'function'
      ) {
        throw new Error('checkout preparation callbacks are missing');
      }

      const cartState = await validateCartItemLimitForCheckout({
        form,
        onLimitExceeded: onCartItemLimitExceeded,
      });
      const latestCartState = await ensureCheckoutLineItemPropertiesForCheckout(cartState);
      /*
       * checkout 直前の更新順序を共通化し、準備に失敗した場合は本来の submit を再開しない。
       */
      await prepareDeliveryAttributes();
      await ensurePointCartAttributesForCheckout();
      await recordContext();
      syncCartFormUpdateControlsWithCartState(form, latestCartState);
      resumeSubmit();
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error && error.message === CART_ITEM_LIMIT_ERROR_MESSAGE
          ? CART_ITEM_LIMIT_ERROR_MESSAGE
          : getCheckoutPreparationErrorMessage();
      window.AuremeToast?.show({
        message,
        type: 'error',
      });
    }
  }

  function clearCheckoutContext() {
    try {
      sessionStorage.removeItem(CHECKOUT_CONTEXT_KEY);
    } catch (error) {
      console.error(error);
    }
  }

  function recordCheckoutContext({ cartState, clearOnMissingToken = false } = {}) {
    try {
      if (cartState?.token) {
        sessionStorage.setItem(
          CHECKOUT_CONTEXT_KEY,
          JSON.stringify({
            token: cartState.token,
            recordedAt: Date.now(),
          }),
        );
        return;
      }

      if (clearOnMissingToken) clearCheckoutContext();
    } catch (error) {
      console.error(error);

      if (clearOnMissingToken) clearCheckoutContext();
    }
  }

  /*
   * セクション再描画で form が差し替えられると保持していた参照は document から
   * 切断されるため、同じ id を持つ現在の要素へ引き直す。
   */
  function resolveConnectedElement(element, expectedType) {
    if (!(element instanceof expectedType)) return null;
    if (element.isConnected) return element;
    if (element.id) return document.getElementById(element.id);
    return null;
  }

  function clearCheckoutReadyFlag(form, readyFlag = CHECKOUT_READY_FLAG) {
    const connectedForm = resolveConnectedElement(form, HTMLFormElement);
    if (!connectedForm) return null;

    delete connectedForm.dataset[readyFlag];
    return connectedForm;
  }

  function runCallbackSafely(callback, ...args) {
    if (typeof callback !== 'function') return undefined;

    try {
      return callback(...args);
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  function resumeCheckoutSubmit({
    form,
    submitter,
    readyFlag = CHECKOUT_READY_FLAG,
    getFallbackSubmitter,
    onReadyFlagSet,
    onRequestSubmitError,
  } = {}) {
    const connectedForm = resolveConnectedElement(form, HTMLFormElement);
    if (!connectedForm) return;

    const connectedSubmitter = resolveConnectedElement(submitter, HTMLElement);

    connectedForm.dataset[readyFlag] = 'true';
    runCallbackSafely(onReadyFlagSet, connectedForm);

    const resolvedSubmitter =
      runCallbackSafely(getFallbackSubmitter, {
        form: connectedForm,
        submitter: connectedSubmitter,
      }) || connectedSubmitter;

    try {
      if (typeof connectedForm.requestSubmit === 'function') {
        if (resolvedSubmitter instanceof HTMLElement) {
          connectedForm.requestSubmit(resolvedSubmitter);
        } else {
          connectedForm.requestSubmit();
        }
        return;
      }
    } catch (error) {
      console.error(error);

      if (typeof onRequestSubmitError === 'function') {
        runCallbackSafely(onRequestSubmitError, error, connectedForm);
        /* 呼び出し元が独自に復旧する場合はここで送信を止め、二重送信を防ぐ */
        return;
      }
      clearCheckoutReadyFlag(connectedForm, readyFlag);
    }

    const temporaryCheckoutInput = document.createElement('input');
    temporaryCheckoutInput.type = 'hidden';
    temporaryCheckoutInput.name = 'checkout';

    /* requestSubmit を使わない送信でも checkout ボタン由来の値を保って、通常のカート送信と同じ遷移に揃える */
    if (
      (resolvedSubmitter instanceof HTMLButtonElement ||
        resolvedSubmitter instanceof HTMLInputElement) &&
      resolvedSubmitter.name === 'checkout' &&
      resolvedSubmitter.value
    ) {
      temporaryCheckoutInput.value = resolvedSubmitter.value;
    }

    connectedForm.appendChild(temporaryCheckoutInput);
    connectedForm.submit();
  }

  return Object.freeze({
    CHECKOUT_CONTEXT_KEY,
    CHECKOUT_READY_FLAG,
    CART_ITEM_LIMIT,
    CART_ITEM_LIMIT_ERROR_MESSAGE,
    parseFooterStateElement,
    getDocumentFooterState,
    fetchFooterState,
    fetchCartState,
    prepareDeliveryAttributesForCheckout,
    buildCartAttributesClearingPointAmount,
    getPointDiscountAmountYen,
    getCheckoutPreparationErrorMessage,
    validateCartItemLimitForCheckout,
    ensureCheckoutLineItemPropertiesForCheckout,
    ensureSaleStatusLineItemPropertiesForCheckout: ensureCheckoutLineItemPropertiesForCheckout,
    prepareAndResumeCheckoutSubmit,
    recordCheckoutContext,
    clearCheckoutContext,
    clearCheckoutReadyFlag,
    resumeCheckoutSubmit,
    ensurePointCartAttributesForCheckout,
  });
})();
